import { useNavigate, useLocation } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import { IoChevronForward } from "react-icons/io5";
import Loader from "src/components/Loader/Loader";
import { useTranslation } from "react-i18next";
import "./details.scss";

// Used by detail pages (/recipients/:id, /patients/:id, /invoices/:id)
function DetailPageDoodle() {
    return (
        <svg
            className="detail-header__doodle"
            viewBox="0 0 900 140"
            preserveAspectRatio="xMidYMid slice"
            xmlns="http://www.w3.org/2000/svg"
        >
            {/* Large arcs top-left */}
            <path d="M -30,0 Q 60,70 0,140" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.1" />
            <path d="M 10,0 Q 110,70 40,140" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.07" />

            {/* Large arcs top-right */}
            <path d="M 930,0 Q 840,70 900,140" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.1" />
            <path d="M 890,0 Q 790,70 860,140" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.07" />

            {/* Center wave */}
            <path
                d="M 160,100 Q 260,60 380,90 Q 500,120 620,85 Q 720,55 820,80"
                fill="none" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" opacity="0.09"
            />
            <path
                d="M 140,115 Q 250,80 370,108 Q 490,136 610,100 Q 710,68 830,95"
                fill="none" stroke="currentColor" strokeWidth="0.6" strokeLinecap="round" opacity="0.06"
            />

            {/* Scattered dots */}
            <circle cx="180" cy="28" r="2.5" fill="currentColor" opacity="0.11" />
            <circle cx="194" cy="42" r="1.5" fill="currentColor" opacity="0.08" />
            <circle cx="340" cy="18" r="1.8" fill="currentColor" opacity="0.09" />
            <circle cx="560" cy="125" r="2" fill="currentColor" opacity="0.1" />
            <circle cx="720" cy="22" r="2.2" fill="currentColor" opacity="0.1" />
            <circle cx="735" cy="36" r="1.4" fill="currentColor" opacity="0.07" />

            {/* Small diamonds */}
            <rect x="440" y="14" width="9" height="9" rx="2" transform="rotate(45 444 18)" fill="none" stroke="currentColor" strokeWidth="0.9" opacity="0.11" />
            <rect x="260" y="110" width="7" height="7" rx="1.5" transform="rotate(45 263 113)" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.09" />
            <rect x="680" y="108" width="7" height="7" rx="1.5" transform="rotate(45 683 111)" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.09" />

            {/* Cross marks */}
            <line x1="124" y1="56" x2="136" y2="56" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" opacity="0.13" />
            <line x1="130" y1="50" x2="130" y2="62" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" opacity="0.13" />
            <line x1="764" y1="56" x2="776" y2="56" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" opacity="0.13" />
            <line x1="770" y1="50" x2="770" y2="62" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" opacity="0.13" />
        </svg>
    );
}

// Used by /settings
export function DetailDoodle() {
    return (
        <svg
            className="detail-header__doodle"
            viewBox="0 0 900 120"
            preserveAspectRatio="xMidYMid slice"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle cx="44" cy="60" r="38" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.12" />
            <circle cx="44" cy="60" r="22" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.08" />
            <circle cx="856" cy="60" r="38" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.12" />
            <circle cx="856" cy="60" r="22" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.08" />
            <circle cx="450" cy="-10" r="48" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.07" />
            <circle cx="140" cy="22" r="2.5" fill="currentColor" opacity="0.12" />
            <circle cx="152" cy="34" r="1.6" fill="currentColor" opacity="0.09" />
            <circle cx="748" cy="88" r="2.5" fill="currentColor" opacity="0.12" />
            <circle cx="736" cy="76" r="1.6" fill="currentColor" opacity="0.09" />
            <circle cx="240" cy="96" r="2" fill="currentColor" opacity="0.1" />
            <circle cx="660" cy="20" r="2" fill="currentColor" opacity="0.1" />
            <line x1="116" y1="60" x2="130" y2="60" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.13" />
            <line x1="123" y1="53" x2="123" y2="67" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.13" />
            <line x1="770" y1="60" x2="784" y2="60" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.13" />
            <line x1="777" y1="53" x2="777" y2="67" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.13" />
            <path
                d="M 190,72 Q 270,44 350,68 Q 430,92 510,66 Q 590,40 670,64 Q 720,78 760,70"
                fill="none" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" opacity="0.08"
            />
            <rect x="188" y="24" width="10" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="0.9" opacity="0.12" />
            <rect x="702" y="82" width="10" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="0.9" opacity="0.12" />
        </svg>
    );
}

// ── DetailPage ────────────────────────────────────────────────────────────────

interface DetailPageProps {
    loading: boolean;
    error: string | null;
    notFoundMessage?: string;
    avatarLetter?: string;
    icon?: React.ReactNode;
    title: string;
    titleNode?: React.ReactNode;
    meta?: React.ReactNode;
    /** Rendered top-right of the header card, opposite the avatar/title/meta block. */
    headerRight?: React.ReactNode;
    breadcrumbActions?: React.ReactNode;
    actions?: React.ReactNode;
    lifecycle?: React.ReactNode;
    accentColor?: string;
    subheader?: React.ReactNode;
    banner?: React.ReactNode;
    tabs?: React.ReactNode;
    children: React.ReactNode;
}

export function DetailPage({
    loading,
    error,
    notFoundMessage,
    avatarLetter,
    icon,
    title,
    titleNode,
    meta,
    headerRight,
    breadcrumbActions,
    actions,
    lifecycle,
    accentColor,
    subheader,
    banner,
    tabs,
    children,
}: DetailPageProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const resolvedNotFoundMessage = notFoundMessage ?? t("common.notFound");

    // Derive parent name from first path segment e.g. /recipients/id → "Recipients"
    const parentName = location.pathname.split("/")[1];
    const parentLabelMap: Record<string, string> = {
        patients:   t("nav.patients"),
        recipients: t("nav.recipients"),
        invoices:   t("nav.invoices"),
        uploads:    t("nav.uploads"),
    };
    const parentLabel = parentLabelMap[parentName] ?? (parentName.charAt(0).toUpperCase() + parentName.slice(1));

    if (loading) return <Loader />;
    if (error) return (
        <div className="empty-state">
            <p>{error}</p>
            <button className="btn-back empty-state__back" onClick={() => navigate(-1)} type="button">
                <IoArrowBack /> {t("common.back")}
            </button>
        </div>
    );
    if (!title) return (
        <div className="empty-state">
            <p>{resolvedNotFoundMessage}</p>
            <button className="btn-back empty-state__back" onClick={() => navigate(-1)} type="button">
                <IoArrowBack /> {t("common.back")}
            </button>
        </div>
    );

    return (
        <div className="page detail-page" style={accentColor ? { "--accent": accentColor } as React.CSSProperties : undefined}>
            <div className="detail-breadcrumb">
                <button className="btn-back" onClick={() => navigate(-1)}>
                    <IoArrowBack /> {t("common.back")}
                </button>
                <span className="detail-breadcrumb__parent" onClick={() => navigate(`/${parentName}`)}>{parentLabel}</span>
                <IoChevronForward className="detail-breadcrumb__sep" />
                <span className="detail-breadcrumb__current">{title}</span>
                {breadcrumbActions && <div className="detail-breadcrumb__actions">{breadcrumbActions}</div>}
            </div>

            <div
                className={`detail-header${lifecycle ? " detail-header--has-lifecycle" : ""}`}
                style={accentColor ? {
                    background: `${accentColor}12`,
                    borderLeft: `4px solid ${accentColor}`,
                    color: accentColor,
                } : undefined}
            >
                <div className="detail-header__doodle-wrap"><DetailPageDoodle /></div>
                <div className="detail-header__top">
                    <div className="detail-header__left">
                        <div
                            className="detail-avatar"
                            style={accentColor ? { background: accentColor } : undefined}
                        >{avatarLetter ?? icon}</div>
                        <div>
                            <h1 className="detail-title">{titleNode ?? title}</h1>
                            {meta && <div className="detail-meta">{meta}</div>}
                        </div>
                    </div>
                    {headerRight && <div className="detail-header__right">{headerRight}</div>}
                </div>
                {lifecycle && (
                    <div className="detail-header__lifecycle">{lifecycle}</div>
                )}
            </div>
            {actions && (
                <div className="detail-header__actions">{actions}</div>
            )}

            {subheader && <div className="detail-subheader">{subheader}</div>}
            {banner && <div className="detail-banner">{banner}</div>}
            {tabs && <div className="detail-tabs">{tabs}</div>}
            <div className="detail-grid">{children}</div>
        </div>
    );
}

// ── DetailCard ────────────────────────────────────────────────────────────────

interface DetailCardProps {
    title: React.ReactNode;
    actions?: React.ReactNode;
    full?: boolean;
    children: React.ReactNode;
}

export function DetailCard({ title, actions, full = false, children }: DetailCardProps) {
    return (
        <div className={`detail-card${full ? " detail-card--full" : ""}`}>
            {actions ? (
                <div className="card-section-title-row">
                    <h3 className="card-section-title">{title}</h3>
                    <div className="card-section-title-row__actions">{actions}</div>
                </div>
            ) : (
                <h3 className="card-section-title">{title}</h3>
            )}
            {children}
        </div>
    );
}

// ── DetailRow ─────────────────────────────────────────────────────────────────

interface DetailRowProps {
    label: React.ReactNode;
    value: React.ReactNode;
    onClick?: () => void;
}

export function DetailRow({ label, value, onClick }: DetailRowProps) {
    return (
        <div className="detail-row">
            <span className="detail-label">{label}</span>
            <span
                className={onClick ? "detail-link" : undefined}
                onClick={onClick}
            >
                {value ?? "-"}
            </span>
        </div>
    );
}
